/**
 * `DcMessage` 契約 ↔ 訊息查詢頁／小鈴鐺的檢視模型（notify-api 票 03–06）。
 *
 * 純轉換、不依賴 React —— 與 `ipContract`／`contractContract` 同一種角色：頁面與 Header
 * 不該各自寫 `m.module_name || m.source` 這種混寫，錯一個欄位就是空白。
 *
 * ## 契約來源（誠實標註）
 * swagger `docs/api/backend_v1.00_swagger.json`（9/29 SIT 版）＋ 9/30 SIT smoke 實測、10/1 重跑一致
 * （`.scratch/notify-api/contract-smoke-20260930.md`）：
 * - `message_category` swagger 是字串 enum，**SIT 實際回整數 0**（2984 則都是）→ 不在六類一律 warn，
 *   只進「全部」，不會從畫面消失（spec 使用者故事 23；後端修正見 sit-smoke-20261001/04）。
 * - `GetCategoryCounts` 回 `[{ value:1..6, text, count }]`（swagger 沒寫型別）。
 * - `status` 實測 `"Active"`。
 *
 * ## API 回什麼就呈現什麼（spec 解法）
 * 語意由後端決定：分類名稱是 `module_name`、管道是 `channels_text`、標籤是 `tag`、
 * 連結是 `action_url`＋`action_text`。前端不替後端推算（不再像舊 mock 那樣從內文猜要去哪個資產頁）。
 */
import { NOTIFY_CATEGORY_BY_TAB } from "../constants/notifyCategories";

/**
 * 六個種類分頁 ↔ 後端 `DcMessageCategory`（enum 名稱）↔ `GetCategoryCounts.value`（1–6，
 * 順序同 enum，9/30 實測 GetCategories）。分頁 key 沿用 `notifyCategories` 的那組（i18n `notify.tabs.*`）。
 */
export const MESSAGE_CATEGORIES = [
  { tab: "unfilled", enumName: "DataIncomplete", value: 1 },
  { tab: "due", enumName: "Expiry", value: 2 },
  { tab: "stock", enumName: "LowStock", value: 3 },
  { tab: "approval", enumName: "Approval", value: 4 },
  { tab: "alert", enumName: "Alert", value: 5 },
  { tab: "version", enumName: "VersionUpdate", value: 6 },
];

// 防呆:分頁 key 必須跟既有分類常數同一組,少一個就是一個點不到的分頁
if (process.env.NODE_ENV !== "production") {
  const known = Object.keys(NOTIFY_CATEGORY_BY_TAB).sort().join();
  const mine = MESSAGE_CATEGORIES.map((c) => c.tab).sort().join();
  if (known !== mine) console.warn(`[messageContract] 分頁 key 與 notifyCategories 不一致:${mine} vs ${known}`);
}

const BY_ENUM = Object.fromEntries(MESSAGE_CATEGORIES.map((c) => [c.enumName, c]));
const BY_TAB = Object.fromEntries(MESSAGE_CATEGORIES.map((c) => [c.tab, c]));
const BY_VALUE = Object.fromEntries(MESSAGE_CATEGORIES.map((c) => [c.value, c]));

/** 分頁 key → GetAll／MarkAllAsRead 的 `messageCategory`；「全部」或不認得的 key → undefined（不帶） */
export const messageCategoryOfTab = (tab) => BY_TAB[tab]?.enumName;

const KNOWN_STATUS = new Set(["Active", "Paused", "Deleted"]);

// 值語意規則(ADR-0004):對不上要吵,但同一個值只吵一次 —— SIT 上 2984 則都是 0,每則都 warn 會洗版
const warned = new Set();
const warnOnce = (key, ...msg) => {
  if (warned.has(key)) return;
  warned.add(key);
  console.warn(...msg);
};
/** 測試用:讓「只 warn 一次」在每支測試重新計數 */
export const resetMessageContractWarnings = () => warned.clear();

/** `message_category` → 分頁 key；不在六類（含整數 0）→ null（只出現在「全部」）並 warn 一次 */
export function categoryTabOf(messageCategory) {
  const hit = BY_ENUM[messageCategory];
  if (hit) return hit.tab;
  warnOnce(
    `category:${JSON.stringify(messageCategory)}`,
    `[messageContract] DcMessage.message_category「${JSON.stringify(messageCategory)}」不在契約的六類`
    + `(${MESSAGE_CATEGORIES.map((c) => c.enumName).join("／")}),這則訊息只會出現在「全部」。`
    + "SIT 目前回整數 0,見 .scratch/sit-smoke-20261001/issues/04。"
  );
  return null;
}


// 前往連結只接受站內路由或 http(s):action_url 是後端資料,`javascript:` 之類的值不能變成可點的東西
const isSafeActionUrl = (u) => u.startsWith("/") || /^https?:\/\//i.test(u);

/** `DcMessageDto` → 一則訊息的檢視模型（<NoticeItem> 吃得下的形狀） */
export function messageToView(m) {
  if (!m) return m;
  if (m.status != null && !KNOWN_STATUS.has(m.status)) {
    warnOnce(
      `status:${JSON.stringify(m.status)}`,
      `[messageContract] DcMessage.status「${JSON.stringify(m.status)}」不在契約(Active／Paused／Deleted)。`
    );
  }
  const actionUrl = (m.action_url || "").trim();
  const actionText = (m.action_text || "").trim();
  if (actionUrl && !isSafeActionUrl(actionUrl)) {
    warnOnce(`url:${actionUrl}`, `[messageContract] DcMessage#${m.id} 的 action_url「${actionUrl}」不是站內路由或 http(s),不顯示前往連結。`);
  }
  return {
    id: m.id,
    // 分類名稱(圖示旁文字)= module_name;SIT 目前是空字串 → 退回後端的 category(例:資安漏洞),
    // 標題列才不會只剩一顆圖示(⚠️ 稿外退路,值仍是後端給的)
    source: m.module_name || m.category || "",
    message: m.content || m.title || "",
    time: m.sent_on || m.created_on || null,
    // 照實呈現:後端 channels_text 已含「通知」(實測「站內＆小鈴噹 通知」),原樣交給 <NoticeItem channelText>,
    // 不拆字、不半形化(0923 稿也是全形「站內＆Email 通知」)
    channelText: m.channels_text || "",
    tag: (m.tag || "").trim(),
    unread: !m.is_read,
    categoryTab: categoryTabOf(m.message_category),
    // 任一為空就不給連結 —— 點了沒反應的連結比沒有連結更糟(使用者故事 13)
    action: actionUrl && actionText && isSafeActionUrl(actionUrl) ? { url: actionUrl, text: actionText } : null,
  };
}

/**
 * GetAll 的查詢參數。`customerId` 必帶 —— 不帶的話後端回全部人的訊息(9/30 實測),
 * 所以沒有登入者時回 null,讓呼叫端不要發這個請求。
 */
export function messageQuery({ customerId, tab = "all", keyword = "", page = 1, pageSize = 6, isRead } = {}) {
  if (!customerId) return null;
  const q = { targetCustomerId: customerId, pageIndex: Math.max(0, page - 1), pageSize };
  const messageCategory = messageCategoryOfTab(tab);
  if (messageCategory) q.messageCategory = messageCategory;
  const kw = String(keyword || "").trim();
  if (kw) q.keyword = kw;
  if (isRead !== undefined) q.isRead = isRead;
  return q;
}

/**
 * `GetCategoryCounts` → `{ [分頁 key]: count }`。`value` 不在 1–6 → warn 並略過
 * （對不上的那筆不知道該掛哪個分頁，掛錯比不掛糟）。
 */
export function categoryCountsToTabs(list) {
  const out = {};
  (Array.isArray(list) ? list : []).forEach((row) => {
    const hit = BY_VALUE[row?.value];
    if (!hit) {
      warnOnce(
        `countValue:${JSON.stringify(row?.value)}`,
        `[messageContract] GetCategoryCounts 的 value「${JSON.stringify(row?.value)}」不在 1–6,這筆筆數不會顯示在任何分頁。`
      );
      return;
    }
    out[hit.tab] = Number(row.count) || 0;
  });
  return out;
}
